import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ConfirmModal from './ConfirmModal';
import PasswordInputField from './PasswordInputField';
import './AdminDashboard.css';

const API_URL = '/api';

function AdminDashboard({ auth }) {
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Modal state for Create / Edit
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [trainerToDelete, setTrainerToDelete] = useState(null);

  // Modal state for Trainer Config (YouTube Playlist & API Key)
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configTrainer, setConfigTrainer] = useState(null);
  const [configData, setConfigData] = useState({
    youtube_playlist_url: '',
    youtube_api_key: ''
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [syncingTrainerId, setSyncingTrainerId] = useState(null);

  useEffect(() => {
    fetchTrainers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTrainers = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${API_URL}/admin/trainers`, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });
      setTrainers(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Error fetching trainers');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setModalMode('create');
    setSelectedTrainer(null);
    setFormData({ name: '', email: '', password: '' });
    setShowModal(true);
    setSuccessMsg('');
    setError(null);
  };

  const handleOpenEditModal = (trainer) => {
    setModalMode('edit');
    setSelectedTrainer(trainer);
    setFormData({
      name: trainer.name,
      email: trainer.email,
      password: ''
    });
    setShowModal(true);
    setSuccessMsg('');
    setError(null);
  };

  const handleOpenConfigModal = (trainer) => {
    setConfigTrainer(trainer);
    setConfigData({
      youtube_playlist_url: trainer.youtube_playlist_url || '',
      youtube_api_key: trainer.youtube_api_key || ''
    });
    setShowApiKey(false);
    setShowConfigModal(true);
    setSuccessMsg('');
    setError(null);
  };

  const handleSaveTrainerConfig = async (e) => {
    if (e) e.preventDefault();
    if (!configTrainer) return;

    try {
      setConfigSaving(true);
      setError(null);
      await axios.put(
        `${API_URL}/admin/trainers/${configTrainer.id}`,
        {
          youtube_playlist_url: configData.youtube_playlist_url,
          youtube_api_key: configData.youtube_api_key
        },
        { headers: { Authorization: `Bearer ${auth?.token}` } }
      );
      setSuccessMsg(`Configuration updated for trainer "${configTrainer.name}".`);
      setShowConfigModal(false);
      fetchTrainers();
    } catch (err) {
      setError(err.response?.data?.error || 'Error saving trainer configuration');
    } finally {
      setConfigSaving(false);
    }
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg('');

    if (!formData.name.trim() || !formData.email.trim()) {
      setError('Name and email are required');
      return;
    }

    if (modalMode === 'create' && !formData.password.trim()) {
      setError('Password is required for new trainer');
      return;
    }

    try {
      if (modalMode === 'create') {
        await axios.post(`${API_URL}/admin/trainers`, formData, {
          headers: { Authorization: `Bearer ${auth?.token}` }
        });
        setSuccessMsg(`Trainer "${formData.name}" created successfully.`);
      } else {
        const payload = {
          name: formData.name,
          email: formData.email
        };
        if (formData.password.trim()) {
          payload.password = formData.password;
        }
        await axios.put(`${API_URL}/admin/trainers/${selectedTrainer.id}`, payload, {
          headers: { Authorization: `Bearer ${auth?.token}` }
        });
        setSuccessMsg(`Trainer "${formData.name}" updated successfully.`);
      }

      setShowModal(false);
      fetchTrainers();
    } catch (err) {
      setError(err.response?.data?.error || 'An error occurred');
    }
  };

  const handleDeleteTrainerClick = (trainer) => {
    setTrainerToDelete(trainer);
  };

  const handleConfirmDeleteTrainer = async () => {
    if (!trainerToDelete) return;
    try {
      setError(null);
      await axios.delete(`${API_URL}/admin/trainers/${trainerToDelete.id}`, {
        headers: { Authorization: `Bearer ${auth?.token}` }
      });
      setSuccessMsg(`Trainer "${trainerToDelete.name}" has been deleted.`);
      setTrainerToDelete(null);
      fetchTrainers();
    } catch (err) {
      setError(err.response?.data?.error || 'Error deleting trainer');
      setTrainerToDelete(null);
    }
  };

  const handleCancelDeleteTrainer = () => {
    setTrainerToDelete(null);
  };

  const handleSyncPlaylist = async (trainerId) => {
    try {
      setSyncingTrainerId(trainerId);
      setError(null);

      // If config modal is open and has unsaved changes, save them before syncing
      if (
        configTrainer &&
        configTrainer.id === trainerId &&
        (configData.youtube_playlist_url !== configTrainer.youtube_playlist_url ||
          configData.youtube_api_key !== configTrainer.youtube_api_key)
      ) {
        await axios.put(
          `${API_URL}/admin/trainers/${trainerId}`,
          {
            youtube_playlist_url: configData.youtube_playlist_url,
            youtube_api_key: configData.youtube_api_key
          },
          { headers: { Authorization: `Bearer ${auth?.token}` } }
        );
      }

      const res = await axios.post(
        `${API_URL}/admin/trainers/${trainerId}/sync-playlist`,
        {},
        { headers: { Authorization: `Bearer ${auth?.token}` } }
      );
      setSuccessMsg(res.data?.message || 'Playlist synced successfully.');
      if (configTrainer && configTrainer.id === trainerId) {
        setConfigTrainer((prev) => ({
          ...prev,
          youtube_playlist_url: configData.youtube_playlist_url,
          youtube_api_key: configData.youtube_api_key,
          exercises_count: res.data?.synced_count ?? prev?.exercises_count
        }));
      }
      fetchTrainers();
    } catch (err) {
      setError(err.response?.data?.error || 'Error syncing playlist');
    } finally {
      setSyncingTrainerId(null);
    }
  };

  return (
    <div className="admin-dashboard-container">
      {error && <div className="error">{error}</div>}
      {successMsg && <div className="success-banner">{successMsg}</div>}

      {/* Trainers table */}
      <div className="trainers-section-card">
        {loading ? (
          <div className="loading">Loading trainers data...</div>
        ) : trainers.length === 0 ? (
          <div className="empty-state">
            No registered trainers found. Click <span>New Trainer</span> to add your first trainer.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="admin-trainers-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Trainer</th>
                  <th>Clients</th>
                  <th style={{ textAlign: 'right' }}>
                    <div className="action-buttons-group">
                      <button
                        className="button button-primary btn-add-trainer"
                        onClick={handleOpenCreateModal}
                      >
                        New Trainer
                      </button>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {trainers.map((trainer) => (
                  <tr key={trainer.id}>
                    <td className="cell-id">#{trainer.id}</td>
                    <td className="cell-name">
                      <span>{trainer.name}</span>
                    </td>
                    <td className="cell-clients-count">
                      {trainer.clients_count || 0}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="action-buttons-group">
                        <button
                          className="button button-secondary btn-sm btn-action-config"
                          onClick={() => handleOpenConfigModal(trainer)}
                          title="Configure YouTube playlist & settings"
                        >
                          Config
                        </button>
                        <button
                          className="button button-secondary btn-sm btn-action-edit"
                          onClick={() => handleOpenEditModal(trainer)}
                          title="Edit trainer details"
                        >
                          Edit
                        </button>
                        <button
                          className="button button-danger btn-sm btn-action-delete"
                          onClick={() => handleDeleteTrainerClick(trainer)}
                          title="Delete trainer"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal for creating / editing trainer */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <h3 className="modal-title">
                {modalMode === 'create' ? 'Create New Trainer' : 'Edit Trainer'}
              </h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowModal(false)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmitForm}>
              <div className="form-group">
                <label htmlFor="trainer-name">Full Name *</label>
                <input
                  id="trainer-name"
                  type="text"
                  maxLength={100}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="trainer-email">Email Address *</label>
                <input
                  id="trainer-email"
                  type="email"
                  maxLength={120}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="trainer-password">
                  {modalMode === 'create' ? 'Password *' : 'New Password'}
                </label>
                <PasswordInputField
                  id="trainer-password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder={modalMode === 'edit' ? 'Leave empty to keep current password' : ''}
                  required={modalMode === 'create'}
                  maxLength={128}
                />
              </div>

              <div className="modal-buttons" style={{ marginTop: '20px' }}>
                <button type="submit" className="button button-primary">
                  {modalMode === 'create' ? 'Create Trainer' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal for Trainer Configuration (YouTube Playlist & API Key & Sync) */}
      {showConfigModal && configTrainer && (
        <div className="modal-overlay">
          <div className="modal-card" style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Trainer Config: {configTrainer.name}</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowConfigModal(false)}
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveTrainerConfig}>
              <div className="form-group">
                <div className="config-label-row">
                  <label htmlFor="config-playlist">YouTube Playlist URL / ID</label>
                  {Boolean(configTrainer && configTrainer.exercises_count !== undefined) && (
                    <span className="config-sync-count">
                      Synced: {configTrainer.exercises_count || 0}
                    </span>
                  )}
                </div>
                <div className="input-with-sync-btn">
                  <input
                    id="config-playlist"
                    type="text"
                    maxLength={500}
                    placeholder="e.g. https://www.youtube.com/playlist?list=PL..."
                    value={configData.youtube_playlist_url}
                    onChange={(e) => setConfigData({ ...configData, youtube_playlist_url: e.target.value })}
                  />
                  <button
                    type="button"
                    className="btn-mini-sync"
                    disabled={!configData.youtube_playlist_url.trim() || syncingTrainerId === configTrainer.id}
                    onClick={() => handleSyncPlaylist(configTrainer.id)}
                    title={syncingTrainerId === configTrainer.id ? 'Syncing...' : 'Sync playlist now'}
                    aria-label="Sync playlist"
                  >
                    <svg
                      className={`sync-icon ${syncingTrainerId === configTrainer.id ? 'spin' : ''}`}
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.3" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="config-api-key">YouTube API Key (Optional)</label>
                <div className="input-with-sync-btn">
                  <input
                    id="config-api-key"
                    type={showApiKey ? 'text' : 'password'}
                    maxLength={255}
                    placeholder="Leave empty to use automatic public RSS feed"
                    value={configData.youtube_api_key}
                    onChange={(e) => setConfigData({ ...configData, youtube_api_key: e.target.value })}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="btn-mini-sync"
                    onClick={() => setShowApiKey(!showApiKey)}
                    title={showApiKey ? 'Hide API key' : 'Show API key'}
                    aria-label={showApiKey ? 'Hide API key' : 'Show API key'}
                  >
                    {showApiKey ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                        <line x1="1" y1="1" x2="23" y2="23"></line>
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="modal-buttons" style={{ marginTop: '20px' }}>
                <button type="submit" className="button button-primary" disabled={configSaving}>
                  {configSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={Boolean(trainerToDelete)}
        message={trainerToDelete ? `Are you sure you want to delete trainer ${trainerToDelete.name}?` : ''}
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleConfirmDeleteTrainer}
        onCancel={handleCancelDeleteTrainer}
      />
    </div>
  );
}

export default AdminDashboard;
